import React, { useState } from 'react';
import { createAuthor, getAuthors, getAuthor, updateAuthor, deleteAuthor, getAuthorBooks } from '../utils/api';
import ResponseDisplay from './ResponseDisplay';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const AuthorForm = () => {
  const [formData, setFormData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    biography: '',
  });
  const [errors, setErrors] = useState({});
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showSampleData, setShowSampleData] = useState(false);

  const validateForm = (action) => {
    const newErrors = {};
    if (!formData.firstName && !formData.id && action === 'create') newErrors.firstName = 'First name is required for creation';
    if (!formData.lastName && !formData.id && action === 'create') newErrors.lastName = 'Last name is required for creation';
    if (!formData.id && ['get', 'update', 'delete', 'getBooks'].includes(action)) {
      newErrors.id = 'Author ID is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const executeAction = async (action) => {
    if (!validateForm(action)) return;
    setLoading(true);
    setLoadingMessage(`${action.charAt(0).toUpperCase() + action.slice(1)} author, please wait... The backend may take up few seconds to wake up.`);

    try {
      let res;
      switch (action) {
        case 'create':
          res = await createAuthor(formData);
          break;
        case 'getAll':
          res = await getAuthors();
          break;
        case 'get':
          res = await getAuthor(formData.id);
          break;
        case 'update':
          res = await updateAuthor(formData.id, formData);
          break;
        case 'delete':
          await deleteAuthor(formData.id);
          res = { data: { message: 'Author deleted successfully' } };
          break;
        case 'getBooks':
          res = await getAuthorBooks(formData.id);
          break;
        default:
          throw new Error('Invalid action');
      }
      setResponse(res.data);
      setError(null);
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} successful!`);
    } catch (err) {
      setError(err.response?.data || { error: `Failed to ${action} author` });
      setResponse(null);
      toast.error(`Failed to ${action} author`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <div className="max-w-4xl p-8 mx-auto mt-24 mb-12 transition-all duration-300 bg-white shadow-xl rounded-2xl">
      {loading && <LoadingSpinner message={loadingMessage} />}
      <h2 className="mb-6 text-3xl font-bold text-blue-600">Manage Authors</h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="relative">
          <label className="block mb-1 text-sm font-medium text-gray-700">Author ID</label>
          <input
            type="text"
            name="id"
            placeholder="Enter Author ID (for Get/Update/Delete)"
            value={formData.id}
            onChange={handleChange}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors.id ? 'border-red-500' : 'border-gray-300'}`}
            disabled={loading}
            aria-describedby="id-error"
          />
          {errors.id && (
            <p id="id-error" className="mt-1 text-xs text-red-500">{errors.id}</p>
          )}
        </div>
        <div className="relative">
          <label className="block mb-1 text-sm font-medium text-gray-700">First Name</label>
          <input
            type="text"
            name="firstName"
            placeholder="Enter First Name"
            value={formData.firstName}
            onChange={handleChange}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
            disabled={loading}
            aria-describedby="firstName-error"
          />
          {errors.firstName && (
            <p id="firstName-error" className="mt-1 text-xs text-red-500">{errors.firstName}</p>
          )}
        </div>
        <div className="relative">
          <label className="block mb-1 text-sm font-medium text-gray-700">Last Name</label>
          <input
            type="text"
            name="lastName"
            placeholder="Enter Last Name"
            value={formData.lastName}
            onChange={handleChange}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
            disabled={loading}
            aria-describedby="lastName-error"
          />
          {errors.lastName && (
            <p id="lastName-error" className="mt-1 text-xs text-red-500">{errors.lastName}</p>
          )}
        </div>
        <div className="relative">
          <label className="block mb-1 text-sm font-medium text-gray-700">Biography</label>
          <input
            type="text"
            name="biography"
            placeholder="Enter Biography"
            value={formData.biography}
            onChange={handleChange}
            className="w-full p-3 transition-all border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mt-8">
        {[
          { action: 'create', label: 'Create', color: 'bg-blue-600 hover:bg-blue-700' },
          { action: 'getAll', label: 'Get All', color: 'bg-green-500 hover:bg-green-600' },
          { action: 'get', label: 'Get by ID', color: 'bg-yellow-500 hover:bg-yellow-600' },
          { action: 'update', label: 'Update', color: 'bg-orange-500 hover:bg-orange-600' },
          { action: 'delete', label: 'Delete', color: 'bg-red-500 hover:bg-red-600' },
          { action: 'getBooks', label: 'Get Books', color: 'bg-purple-500 hover:bg-purple-600' },
        ].map(({ action, label, color }) => (
          <button
            key={action}
            onClick={() => executeAction(action)}
            disabled={loading}
            className={`${color} text-white px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={label}
          >
            {loading ? 'Processing...' : label}
          </button>
        ))}
      </div>
      <div className="mt-8">
        <button
          onClick={() => setShowSampleData(!showSampleData)}
          className="flex items-center font-semibold text-blue-600 transition-all hover:text-blue-800"
          aria-expanded={showSampleData}
          aria-controls="sample-data"
        >
          {showSampleData ? <FaChevronUp className="mr-2" /> : <FaChevronDown className="mr-2" />}
          Sample Data
        </button>
        {showSampleData && (
          <div id="sample-data" className="p-6 mt-4 bg-gray-50 rounded-xl animate-fade-in">
            <h3 className="mb-4 text-lg font-semibold text-gray-800">Sample Author Data</h3>
            <pre className="p-4 overflow-auto font-mono text-sm text-gray-800 bg-white rounded-lg">
              {JSON.stringify(
                {
                  firstName: "J.K.",
                  lastName: "Rowling",
                  biography: "Author of Harry Potter",
                },
                null,
                2
              )}
            </pre>
          </div>
        )}
      </div>
      <ResponseDisplay response={response} error={error} />
    </div>
  );
};

export default AuthorForm;